/**
 * CloudWatch RUM configuration for the LangHub UI.
 *
 * Values are injected at **website-deploy time** by Katal `StaticCopy` (`{{Token}}` placeholders
 * rewritten from the website stack's RumNestedStack CloudFormation values — see webpack.standalone.config.js
 * and the CDK `deployTimeReplacements`). The SAME pre-built artifact is used for every stage
 * (alpha/beta/gamma/prod); each stage's website deploy substitutes its own real values, so there is
 * no build-time bake and no per-stage hardcoded config.
 *
 * RUM uses the **Midway** flow, so the app monitor id, the Midway monitoring role ARN, and the
 * relying party are all required.
 */

// Katal deploy-time replacement tokens. On a deployed site these are substituted with real values;
// on the local dev server they resolve to the `developmentValue` (empty) so RUM stays disabled.
const RUM_APP_MONITOR_ID = '834363e3-e8ad-48cc-9b78-dd054b886be6';
const RUM_MONITORING_ROLE_ARN = 'arn:aws:iam::533678650081:role/LangHubUIWebsite-RumStackNest-RumMidwayRole9A1D1545-cjWtYIp2r6qj';
const RUM_RELYING_PARTY = 'https://gamma.langhub.localization.amazon.dev';
const RUM_REGION = 'us-west-2';

export interface RumConfig {
  /** RUM app monitor id (the `AWS::RUM::AppMonitor` `Id` attribute). */
  readonly applicationId: string;
  /** Region the app monitor lives in. */
  readonly region: string;
  /**
   * The Midway web-identity role ARN the RUM web client assumes (via STS
   * `AssumeRoleWithWebIdentity`) to obtain temporary `rum:PutRumEvents` credentials.
   */
  readonly monitoringRoleArn: string;
  /**
   * The Midway relying-party origin (OIDC audience), e.g. `https://langhub.localization.amazon.dev`.
   * MUST match the value the CDK registers on the OIDC provider / role trust policy.
   */
  readonly relyingParty: string;
}

/**
 * True when a Katal `{{Token}}` placeholder was NOT substituted at deploy time (or is empty). Such a
 * value must be treated as "not configured" so RUM fails safe (disabled) rather than initializing
 * with a literal `{{...}}` string. Covers the local dev server (empty developmentValue) and any
 * stage whose deploy-time replacement was omitted.
 */
function isUnresolved(value: string): boolean {
  return !value || value.startsWith('{{');
}

/**
 * Resolves the RUM configuration from the deploy-time-injected values.
 *
 * Returns `null` when RUM is not configured for this deployment (any of the required values is
 * missing or an unreplaced placeholder) — callers must treat `null` as "RUM disabled" and skip
 * initialization rather than constructing a client with blanks/placeholders.
 */
export function getRumConfig(): RumConfig | null {
  if (
    isUnresolved(RUM_APP_MONITOR_ID) ||
    isUnresolved(RUM_MONITORING_ROLE_ARN) ||
    isUnresolved(RUM_RELYING_PARTY)
  ) {
    return null;
  }

  /* istanbul ignore next -- untestable: the RUM_* values are module-level Katal
     `{{Token}}` placeholders that are only substituted with real values at
     website-deploy time. In tests they are never resolved, so isUnresolved()
     above always short-circuits to null and this success return is unreachable. */
  return {
    applicationId: RUM_APP_MONITOR_ID,
    region: RUM_REGION,
    monitoringRoleArn: RUM_MONITORING_ROLE_ARN,
    relyingParty: RUM_RELYING_PARTY,
  };
}
